import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Bot, User, Headphones, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const AGENT_KEYWORDS = [
  'live agent', 'real person', 'human support', 'speak to a human',
  'talk to a human', 'real agent', 'speak to a person', 'connect to live agent',
  'talk to a live person', 'human agent', 'live support agent',
  'actual person', 'real human', 'live person'
];

function detectAgentIntent(text) {
  const lower = text.toLowerCase();
  if (lower.includes('[connect_agent]')) return true;
  return AGENT_KEYWORDS.some(kw => lower.includes(kw));
}

export function AIAgentWidget() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('ai'); // 'ai' | 'confirm' | 'connecting' | 'ticket'
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi there! I am your AI assistant. How can I help you navigate the website today?' }
  ]);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [ticketStatus, setTicketStatus] = useState(null);
  const [agentName, setAgentName] = useState(null);
  const [pendingQuestion, setPendingQuestion] = useState('');
  const messagesEndRef = useRef(null);
  const ticketEndRef = useRef(null);
  const pollRef = useRef(null);

  const scrollToBottom = () => {
    if (mode === 'ticket' || mode === 'connecting') {
      ticketEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => { scrollToBottom(); }, [messages, ticketMessages, mode]);

  // Poll for ticket messages
  useEffect(() => {
    if (!user || !ticketId || (mode !== 'ticket' && mode !== 'connecting')) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/tickets/${ticketId}/messages`);
        setTicketMessages(data.messages || []);

        // Check ticket status
        try {
          const statusRes = await api.get('/tickets/active');
          const statusData = statusRes.data;
          if (statusData.ticket) {
            setTicketStatus(statusData.ticket.status);
            if (statusData.ticket.claimed_by && !agentName) {
              setAgentName(statusData.ticket.claimed_by);
              setMode('ticket');
            }
            if (statusData.ticket.status === 'closed') {
              clearInterval(pollRef.current);
            }
          } else {
            // Ticket was deleted/closed
            setTicketStatus('closed');
            clearInterval(pollRef.current);
          }
        } catch (e) { /* status check failed */ }
      } catch (e) { console.error('Poll error:', e); }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [user, ticketId, mode, agentName]);

  // Check for existing active ticket on mount ONLY if user is logged in
  useEffect(() => {
    if (!user) return;
    const checkActive = async () => {
      try {
        const { data } = await api.get('/tickets/active');
        if (data.ticket && data.ticket.status !== 'closed') {
          setTicketId(data.ticket.ticket_id);
          setTicketStatus(data.ticket.status);
          if (data.ticket.claimed_by) {
            setAgentName(data.ticket.claimed_by);
            setMode('ticket');
          } else {
            setMode('connecting');
          }
        }
      } catch (e) {}
    };
    checkActive();
  }, [user]);

  const handleSend = async () => {
    if (!input.trim()) return;
    const userText = input;
    const userMsg = { role: 'user', content: userText };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);
    try {
      const apiMessages = updatedMessages.map(m => ({
        role: m.role,
        content: m.content
      }));
      const { data } = await api.post('/agent/chat', { messages: apiMessages });
      let botContent = typeof data.response === 'string' ? data.response : JSON.stringify(data.response);
      
      if (detectAgentIntent(botContent) || detectAgentIntent(input)) {
        setPendingQuestion(input);
        setMode('confirm');
        setMessages(prev => [...prev, { role: 'assistant', content: 'I can connect you to a live support agent. Would you like that?' }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: botContent }]);
      }
    } catch (err) {
      if (detectAgentIntent(input)) {
        setPendingQuestion(input);
        setMode('confirm');
        setMessages(prev => [...prev, { role: 'assistant', content: 'I can connect you to a live support agent. Would you like that?' }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I am having trouble right now. Please try again later.' }]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmAgent = async (yes) => {
    if (!yes) {
      setMode('ai');
      setMessages(prev => [...prev, { role: 'assistant', content: 'No problem! I\'m still here to help. What else can I do for you?' }]);
      return;
    }
    if (!user) {
      setMode('ai');
      setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ You need to be logged in to connect to a live agent. Please log in first!' }]);
      return;
    }
    setMode('connecting');
    try {
      const { data } = await api.post('/tickets/create', { question: pendingQuestion });
      setTicketId(data.ticket_id);
      setTicketStatus('open');
    } catch (err) {
      setMode('ai');
      if (err?.response?.status === 401) {
        setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ You need to be logged in to connect to a live agent. Please log in first!' }]);
      } else {
        const detail = err?.response?.data?.detail || err.message || 'Unknown error';
        setMessages(prev => [...prev, { role: 'assistant', content: `Sorry, couldn't connect you: ${detail}. Please try again later.` }]);
      }
    }
  };

  const handleTicketSend = async () => {
    if (!input.trim() || !ticketId) return;
    const msg = input;
    setInput('');
    try {
      await api.post(`/tickets/${ticketId}/message`, { content: msg });
    } catch (e) { console.error('Send error:', e); }
  };

  const handleCloseTicket = async () => {
    if (!ticketId) return;
    try {
      await api.post(`/tickets/${ticketId}/close`);
      setTicketStatus('closed');
    } catch (e) {}
  };

  const handleBackToAI = () => {
    setMode('ai');
    setTicketId(null);
    setTicketStatus(null);
    setAgentName(null);
    setTicketMessages([]);
    clearInterval(pollRef.current);
  };

  // ─── RENDER ───
  const renderConnecting = () => (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center animate-pulse">
          <Headphones className="w-10 h-10 text-white" />
        </div>
        <div className="absolute inset-0 w-20 h-20 rounded-full border-4 border-blue-400 animate-ping opacity-30" />
      </div>
      <h3 className="font-bold text-lg mb-2 dark:text-white">Connecting you to an agent...</h3>
      <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">This may take a moment. An agent will be with you shortly.</p>
      <p className="text-sm text-zinc-600 dark:text-zinc-300 bg-blue-50 dark:bg-blue-900/30 rounded-xl p-4 leading-relaxed">
        💬 Please describe your issue below and we'll get back to you as soon as possible!
      </p>
      {/* Show messages sent while waiting */}
      <div className="w-full mt-4 space-y-2 max-h-40 overflow-y-auto">
        {ticketMessages.map((msg, i) => (
          <div key={i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`px-3 py-2 rounded-2xl text-sm max-w-[85%] ${
              msg.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-zinc-100 dark:bg-zinc-700 dark:text-white'
            }`}>{msg.content}</div>
          </div>
        ))}
        <div ref={ticketEndRef} />
      </div>
    </div>
  );

  const renderTicketChat = () => (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      {/* Agent joined notice */}
      {agentName && (
        <div className="text-center py-2">
          <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-3 py-1 rounded-full font-medium">
            ✅ {agentName} has joined the chat
          </span>
        </div>
      )}
      {ticketMessages.map((msg, i) => (
        <div key={i} className={`flex gap-2 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
          {msg.sender === 'agent' && (
            <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">
              {(msg.sender_name || 'A')[0]}
            </div>
          )}
          <div className={`px-4 py-2 rounded-2xl max-w-[80%] text-sm ${
            msg.sender === 'user'
              ? 'bg-blue-600 text-white rounded-br-none'
              : 'bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-100 rounded-bl-none'
          }`}>
            {msg.sender === 'agent' && msg.sender_name && (
              <div className="text-xs font-bold text-green-600 dark:text-green-400 mb-0.5">{msg.sender_name}</div>
            )}
            {msg.content}
          </div>
        </div>
      ))}
      {ticketStatus === 'closed' && (
        <div className="text-center py-4">
          <div className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-300 px-4 py-2 rounded-full font-medium inline-block">
            🔒 This ticket has been closed. Thank you!
          </div>
        </div>
      )}
      <div ref={ticketEndRef} />
    </div>
  );

  const renderConfirmation = () => (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-4">
        <Headphones className="w-8 h-8 text-white" />
      </div>
      <h3 className="font-bold text-lg mb-2 dark:text-white">Connect to a live agent?</h3>
      <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6">A real person from our support team will assist you.</p>
      <div className="flex gap-3">
        <button
          onClick={() => handleConfirmAgent(false)}
          className="px-6 py-2.5 rounded-full border border-zinc-300 dark:border-zinc-600 text-sm font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors dark:text-white"
        >
          No, go back
        </button>
        <button
          onClick={() => handleConfirmAgent(true)}
          className="px-6 py-2.5 rounded-full bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/25"
        >
          Yes, connect me
        </button>
      </div>
    </div>
  );

  const isTicketMode = mode === 'ticket' || mode === 'connecting';
  const isClosed = ticketStatus === 'closed';

  return (
    <div className="fixed bottom-24 right-5 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end">
      {isOpen && (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl w-[calc(100vw-2.5rem)] max-w-[380px] h-[520px] flex flex-col mb-4 overflow-hidden animate-in slide-in-from-bottom-5">
          {/* Header */}
          <div className={`p-4 flex justify-between items-center text-white ${
            isTicketMode ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-blue-600 to-indigo-600'
          }`}>
            <div className="flex items-center gap-2">
              {isTicketMode && (
                <button onClick={handleBackToAI} className="hover:bg-white/20 p-1 rounded-full transition-colors mr-1">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              {isTicketMode ? <Headphones className="w-5 h-5" /> : <Bot className="w-5 h-5" />}
              <div>
                <h3 className="font-semibold text-sm">{isTicketMode ? 'Live Support' : 'TANTA Assistant'}</h3>
                {isTicketMode && (
                  <span className="text-xs opacity-80">
                    {ticketStatus === 'claimed' ? `${agentName || 'Agent'} is here` : ticketStatus === 'closed' ? 'Closed' : 'Waiting for agent...'}
                  </span>
                )}
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          {mode === 'confirm' ? renderConfirmation() :
           mode === 'connecting' ? renderConnecting() :
           mode === 'ticket' ? renderTicketChat() : (
            /* AI Chat Messages */
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-5 h-5" />
                    </div>
                  )}
                  <div className={`px-4 py-2 rounded-2xl max-w-[80%] text-sm ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-none'
                      : 'bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-100 rounded-bl-none'
                  }`}>{msg.content}</div>
                </div>
              ))}
              {isLoading && (
                <div className="flex gap-2 justify-start">
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5" />
                  </div>
                  <div className="px-4 py-3 rounded-2xl bg-zinc-100 dark:bg-zinc-800 rounded-bl-none flex items-center gap-1">
                    <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}} />
                    <div className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{animationDelay: '0.4s'}} />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input area — shown in AI mode, connecting mode, and ticket mode (unless closed) */}
          {(mode === 'ai' || ((mode === 'ticket' || mode === 'connecting') && !isClosed)) && (
            <div className="p-4 border-t border-zinc-200 dark:border-zinc-800">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (isTicketMode ? handleTicketSend() : handleSend())}
                  placeholder={isTicketMode ? 'Describe your issue...' : 'Ask me anything...'}
                  className="flex-1 bg-zinc-100 dark:bg-zinc-800 border-transparent rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all dark:text-white"
                />
                <Button
                  onClick={isTicketMode ? handleTicketSend : handleSend}
                  disabled={isLoading || !input.trim()}
                  size="icon"
                  className={`rounded-full h-10 w-10 ${isTicketMode ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  <Send className="w-4 h-4 text-white" />
                </Button>
              </div>
            </div>
          )}

          {/* Closed ticket — back button */}
          {isClosed && isTicketMode && (
            <div className="p-4 border-t border-zinc-200 dark:border-zinc-800">
              <button
                onClick={handleBackToAI}
                className="w-full py-2.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-sm font-medium hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors dark:text-white"
              >
                ← Back to AI Assistant
              </button>
            </div>
          )}
        </div>
      )}

      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all flex items-center justify-center"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
